import { observable, action, computed, configure, runInAction } from 'mobx';
import agent from '../api/agent';
import { createContext } from 'react';
import { ICalculation } from '../models/calculation';
import { IOptions } from '../models/options';
import { IExternalPrices } from '../models/externalPrices';
import { useLocation } from 'react-router-dom';

configure({ enforceActions: 'always' });

class CalculatorStore {
  @observable loadingInitial = false;
  // CALCULATION
  @observable calculation: ICalculation | null = null;
  @observable currentDate: string = '';
  @observable currentPrice = 0;
  @observable currentDeliveryTime = 0;
  @observable deliveryTimeDays: string = '0';
  @observable currentPriceBRL: string = 'R$ 0,00';

  // OPTIONS
  @observable options: IOptions;
  @observable typesOfCertificates: any[] = [];
  @observable typesOfProperties: any[] = [];
  @observable typesOfCitizenCertificates: any[] = [];
  @observable typesOfEmission: any[] = [];
  @observable statesOfBrazil: any[] = [];
  @observable externalPrices: IExternalPrices;

  @observable mainLink: string;

  @action loadPrices = async () => {
    this.loadingInitial = true;

    this.setUrlFromParams();

    try {
      const prices = await agent.Prices.list();
      runInAction('loading prices', () => {
        this.options = prices.options;
        this.externalPrices = prices.externalPrices;

        const {
          typesOfCertificates,
          typesOfCitizenCertificates,
          typesOfEmission,
          typesOfProperties,
          statesOfBrazil,
        } = this.options;

        this.typesOfCertificates = typesOfCertificates;
        this.typesOfProperties = typesOfProperties;
        this.typesOfCitizenCertificates = typesOfCitizenCertificates;
        this.typesOfEmission = typesOfEmission;
        this.statesOfBrazil = statesOfBrazil;

        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction('loading prices error', () => {
        this.loadingInitial = false;
      });
      console.log(error);
    }
  };

  // UPDATE STATE
  @action updateCalculation = (calculation: ICalculation) => {
    this.calculation = calculation;
    this.updateCurrentPrice();
  };

  @action updateDate = (date: string) => {
    this.currentDate = date;
    this.updateCurrentPrice();
  };

  @action updateIsHaiaCheckbox = (checked: any) => {
    this.calculation!.isHaia = checked;
    this.updateCurrentPrice();
  };

  // CLEAR ACTIONS

  @action clearAllFields = () => {
    this.clearCertificateType();
    this.clearCitizenCertificateType();
    this.clearEmissionType();
    this.clearPropertyType();
    this.clearHaiaCheckbox();
    this.clearStateOfBrazil();
    this.clearDate();

    this.currentPrice = 0;
    this.currentPriceBRL = 'R$ 0,00';
    this.calculation = null;
  };

  @action clearCalculation = () => {
    this.clearCitizenCertificateType();
    this.clearEmissionType();
    this.clearPropertyType();
    this.clearHaiaCheckbox();
    this.clearStateOfBrazil();
    this.clearDate();
  };
  @action clearPropertyType = () => {
    if (this.calculation !== null && this.calculation.typeOfProperty) {
      this.calculation!.typeOfProperty = '';
    }
  };
  @action clearCertificateType = () => {
    if (this.calculation !== null) this.calculation!.typeOfCertificate = '';
  };
  @action clearCitizenCertificateType = () => {
    if (this.calculation !== null && this.calculation.typeOfCitizenCertificate)
      this.calculation!.typeOfCitizenCertificate = undefined;
  };
  @action clearEmissionType = () => {
    if (this.calculation !== null && this.calculation.typeOfEmission)
      this.calculation!.typeOfEmission = '';
  };
  @action clearStateOfBrazil = () => {
    if (this.calculation !== null) {
      this.calculation!.state = '';
    }
  };
  @action clearHaiaCheckbox = () => {
    if (this.calculation !== null && this.calculation.isHaia) this.calculation!.isHaia = false;
  };
  @action clearDate = () => {
    if (this.calculation !== null && this.currentDate) this.currentDate = '';
  };

  // CALCULATE ACTIONS AND METHODS
  @action updateCurrentPrice = () => {
    this.currentPrice = 0;
    this.currentDeliveryTime = 0;

    const calculateCertificateType = this.calculateCertificateTypePrice();
    const calculatePropertyType = this.calculatePropertyTypePrice();
    const calculateCitizenCertificateType = this.calculateCitizenCertificateTypePrice();
    const calculateEmissionType = this.calculateEmissionTypePrice();
    const calculateHaia = this.calculateHaiaPrice();

    this.currentPrice =
      calculateCertificateType.price +
      calculatePropertyType.price +
      calculateCitizenCertificateType.price +
      calculateEmissionType.price +
      calculateHaia.price;

    this.currentDeliveryTime =
      calculateCertificateType.deliveryTime +
      calculatePropertyType.deliveryTime +
      calculateCitizenCertificateType.deliveryTime +
      calculateEmissionType.deliveryTime +
      calculateHaia.deliveryTime;

    if (this.currentDeliveryTime !== 0) {
      this.deliveryTimeDays = this.formatPrazoToDays();
    }

    this.currentPriceBRL = this.formatToBRLCurrency(this.currentPrice);
  };

  private formatPrazoToDays(): string {
    if (!this.currentDeliveryTime || this.currentDeliveryTime === 0) return '0';
    let diaUtilMin = this.currentDeliveryTime;
    let diaUtilMax = this.currentDeliveryTime + 10;

    if (['nascimento', 'casamento', 'obito'].includes(this.calculation?.typeOfCertificate)) {
      if (
        this.calculation?.typeOfCitizenCertificate &&
        'eletronica' === this.calculation?.typeOfCitizenCertificate
      ) {
        diaUtilMin = 7;
        diaUtilMax = this.currentDeliveryTime;
      }
    } else {
      diaUtilMin = 7;
      diaUtilMax = this.currentDeliveryTime;
    }

    return `${diaUtilMin} a ${diaUtilMax} dias úteis`;
  }
  private formatToBRLCurrency(currentPrice: number): string {
    return currentPrice.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      style: 'currency',
      currency: 'BRL',
    });
  }

  private calculateCertificateTypePrice(): { deliveryTime: number; price: number } {
    if (!this.typesOfCertificates || !this.calculation?.typeOfCertificate)
      return {
        price: 0,
        deliveryTime: 0,
      };

    const currentPriceOfCertificate = this.typesOfCertificates.find(
      (x: any) => this.calculation?.typeOfCertificate === x.key,
    ).price;

    if (!currentPriceOfCertificate && currentPriceOfCertificate !== 0)
      return {
        price: 0,
        deliveryTime: 0,
      };

    let valueToAdd = 0;

    // CUSTOM VALUES PRICES
    const certificatesCustomPrices = this.options.typesOfCertificates.find(
      (x) => x.value === this.calculation?.typeOfCertificate,
    )?.customPrices;

    if (certificatesCustomPrices) {
      valueToAdd += this.calculateDateCustomPrice(certificatesCustomPrices);
      valueToAdd += this.calculateStateCustomPrice(certificatesCustomPrices);
    }

    return {
      price: valueToAdd + currentPriceOfCertificate,
      deliveryTime:
        this.options.typesOfCertificates.find(
          (x) => x.value === this.calculation?.typeOfCertificate,
        )?.deliveryTime || 0,
    };
  }

  private calculatePropertyTypePrice(): {
    price: number;
    deliveryTime: number;
  } {
    if (!this.typesOfCertificates || !this.calculation?.typeOfProperty)
      return {
        price: 0,
        deliveryTime: 0,
      };

    const currentPriceOfProperty = this.typesOfProperties.find(
      (x: any) => this.calculation?.typeOfProperty === x.key,
    ).price;

    if (!currentPriceOfProperty && currentPriceOfProperty !== 0)
      return {
        price: 0,
        deliveryTime: 0,
      };

    let valueToAdd = 0;

    // CUSTOM VALUES PRICES
    const propertyCertificatesCustomPrices = this.options.typesOfProperties.find(
      (x) => x.value === this.calculation?.typeOfProperty,
    )?.customPrices;

    if (propertyCertificatesCustomPrices) {
      valueToAdd += this.calculateDateCustomPrice(propertyCertificatesCustomPrices);
      valueToAdd += this.calculateStateCustomPrice(propertyCertificatesCustomPrices);
    }

    return {
      price: valueToAdd + currentPriceOfProperty,
      deliveryTime:
        this.options.typesOfProperties.find((x) => x.value === this.calculation?.typeOfProperty)
          ?.deliveryTime || 0,
    };
  }

  private calculateCitizenCertificateTypePrice(): {
    price: number;
    deliveryTime: number;
  } {
    if (!this.typesOfCitizenCertificates || !this.calculation?.typeOfCitizenCertificate)
      return {
        price: 0,
        deliveryTime: 0,
      };

    const currentPriceOfCitizenCertificate = this.typesOfCitizenCertificates.find(
      (x: any) => this.calculation?.typeOfCitizenCertificate === x.key,
    ).price;

    if (!currentPriceOfCitizenCertificate && currentPriceOfCitizenCertificate !== 0)
      return {
        price: 0,
        deliveryTime: 0,
      };

    let valueToAdd = 0;

    // CUSTOM VALUES PRICES
    const citizenCertificatesCustomPrices = this.options.typesOfCitizenCertificates.find(
      (x) => x.value === this.calculation?.typeOfCitizenCertificate,
    )?.customPrices;

    if (citizenCertificatesCustomPrices) {
      valueToAdd += this.calculateDateCustomPrice(citizenCertificatesCustomPrices);
      valueToAdd += this.calculateStateCustomPrice(citizenCertificatesCustomPrices);
    }

    return {
      price: valueToAdd + currentPriceOfCitizenCertificate,
      deliveryTime:
        this.options.typesOfCitizenCertificates.find(
          (x) => x.value === this.calculation?.typeOfCitizenCertificate,
        )?.deliveryTime || 0,
    };
  }

  private calculateEmissionTypePrice(): {
    price: number;
    deliveryTime: number;
  } {
    if (!this.typesOfEmission || !this.calculation?.typeOfEmission)
      return {
        price: 0,
        deliveryTime: 0,
      };

    const currentPriceOfEmission = this.typesOfEmission.find(
      (x: any) => this.calculation?.typeOfEmission === x.key,
    ).price;

    if (!currentPriceOfEmission && currentPriceOfEmission !== 0)
      return {
        price: 0,
        deliveryTime: 0,
      };

    let valueToAdd = 0;

    // CUSTOM VALUES PRICES
    const currentEmissionCustomPrices = this.options.typesOfEmission.find(
      (x) => x.value === this.calculation?.typeOfEmission,
    )?.customPrices;

    if (currentEmissionCustomPrices) {
      valueToAdd += this.calculateDateCustomPrice(currentEmissionCustomPrices);
      valueToAdd += this.calculateStateCustomPrice(currentEmissionCustomPrices);
    }

    return {
      price: valueToAdd + currentPriceOfEmission,
      deliveryTime:
        this.options.typesOfEmission.find((x) => x.value === this.calculation?.typeOfEmission)
          ?.deliveryTime || 0,
    };
  }

  private calculateHaiaPrice(): {
    price: number;
    deliveryTime: number;
  } {
    if (!this.calculation?.isHaia)
      return {
        price: 0,
        deliveryTime: 0,
      };

    const currentHaiaCustomPrices = this.externalPrices.isHaia.customPrices;

    if (currentHaiaCustomPrices) {
      let addStateValue = currentHaiaCustomPrices.states.find(
        (state) => state.value === this.calculation?.state,
      );
      return {
        price: addStateValue ? addStateValue.price : this.externalPrices.isHaia.price,
        deliveryTime: this.externalPrices.isHaia.deliveryTime,
      };
    }

    return {
      price: this.externalPrices.isHaia.price,
      deliveryTime: this.externalPrices.isHaia.deliveryTime,
    };
  }

  // CUSTOM PRICE METHODS
  private calculateStateCustomPrice(customPrices: any): number {
    if (customPrices.states) {
      let addStateValue = customPrices.states.find(
        (state: { value: string }) => state.value === this.calculation?.state,
      );
      return addStateValue ? addStateValue.price : 0;
    }
    return 0;
  }

  private calculateDateCustomPrice(customPrices: any): number {
    if (customPrices.olderThan) {
      const year: number = customPrices.olderThan.year;
      const price: number = customPrices.olderThan.price;
      let excludeState: boolean = customPrices.olderThan.exceptFor.includes(
        this.calculation!.state,
      );

      if (excludeState) return 0;

      if (Number.parseInt(this.currentDate) < year) {
        return price;
      }
      return 0;
    }
    return 0;
  }

  @computed get paymentInstallments() {
    let installments = this.currentPrice / 3;
    return this.formatToBRLCurrency(installments);
  }

  @computed get shipping() {
    if (this.calculation?.typeOfCitizenCertificate === 'eletronica') return '+ envio grátis';

    return ' + frete grátis';
  }

  // HIDE COMPONENTS
  @computed get hideDate() {
    if (this.calculation === null) return true;
    if (
      !['nascimento', 'casamento', 'obito'].includes(this.calculation?.typeOfCertificate) ||
      this.calculation?.typeOfCitizenCertificate === 'eletronica'
    )
      return true;

    return false;
  }

  @computed get hideTypesOfProperties() {
    if (this.calculation === null) return true;
    if (this.calculation?.typeOfCertificate !== 'imovel') return true;
    return false;
  }

  @computed get hideTypesOfCitizenCertificates() {
    if (this.calculation === null) return true;
    if (!['nascimento', 'casamento', 'obito'].includes(this.calculation?.typeOfCertificate))
      return true;
    return false;
  }

  @computed get hideTypesOfEmission() {
    if (this.calculation === null) return true;
    if (
      !['nascimento', 'casamento', 'obito'].includes(this.calculation?.typeOfCertificate) ||
      this.calculation?.typeOfCitizenCertificate !== 'inteiroteor'
    )
      return true;
    return false;
  }

  @computed get hideExternalPrices() {
    if (this.calculation === null) return true;
    if (
      !['nascimento', 'casamento', 'obito'].includes(this.calculation?.typeOfCertificate) ||
      this.calculation?.typeOfCitizenCertificate === 'eletronica'
    )
      return true;
    return false;
  }

  // GET LINK METHODS
  private setUrlFromParams() {
    const queryParams = new URLSearchParams(window.location.search);

    let site = queryParams.get('site');
    let id = queryParams.get('id');

    let utm_source = queryParams.get('utm_source') || '';
    let utm_medium = queryParams.get('utm_medium') || '';
    let utm_campaign = queryParams.get('utm_campaign') || '';
    let utm_content = queryParams.get('utm_content') || '';

    let utm_params =
      '' +
      (utm_source ? `utm_source=${utm_source}` : '') +
      (utm_medium ? `&utm_medium=${utm_medium}` : '') +
      (utm_campaign ? `&utm_campaign=${utm_campaign}` : '') +
      (utm_content ? `&utm_content=${utm_content}` : '');

    if (utm_params) {
      utm_params = '?' + utm_params;
    }

    if (!site) {
      site = 'pedido.certidaoonlinebrasil.com.br';
    }
    if (!id) {
      id = 'b3e426bc-ba72-45ae-8436-67c875b8d6b9';
    }
    if (utm_params) {
      id = id + utm_params;
    }
    this.mainLink = `https://${site}/solicitar/${id}`;
  }
}

export default createContext(new CalculatorStore());
